/* eslint-disable react/jsx-props-no-spreading */

import React from 'react';
import classNames from 'classnames';
import { useDropzone } from 'react-dropzone';
import { ReactSortable } from 'react-sortablejs';

import Button, { ButtonType } from 'ui/Button';

import { DATA_TEST_IDS } from './consts';
import { MediaUploaderProps, SortableMediaUploaderItem } from './types';
import VideoMediaUploaderItem from './VideoMediaUploaderItem';
import styles from './index.module.scss';

export type { MediaUploaderProps } from './types';

const VideoMediaUploader: React.FC<MediaUploaderProps> = ({
  className,
  multiple = true,
  isSortable = false,
  accept,
  items,
  isLoading,
  elementId,
  buttonTitle,
  description,
  isMediaUploaderVisible = true,
  onDrop,
  onRemove,
  onRetry,
  onReorder,
  renderer: MediaListRenderer,
  postfix: MediaListRendererChildren,
}) => {
  const dropzone = useDropzone({
    multiple,
    accept,
    onDrop,
  });

  const onClickUpload = () => {
    dropzone.open();
  };

  const sortableItems = items.map<SortableMediaUploaderItem>((item, index) => ({
    ...item,
    id: item.value.id || `video-media-uploader-${index}`,
  }));
  const isSortableEnabled = isSortable && Boolean(onReorder);
  const isDragHandleVisible = isSortableEnabled && items.length > 1;
  const mediaListItems = (isSortableEnabled ? sortableItems : items).map((item, index) => (
    <VideoMediaUploaderItem
      key={item.value.id || `video-media-uploader-${index}`}
      item={item}
      sortableId={isSortableEnabled ? (item as SortableMediaUploaderItem).id : undefined}
      elementId={elementId}
      isLoading={isLoading}
      isDragHandleVisible={isDragHandleVisible}
      onRemove={onRemove}
      onRetry={onRetry}
      renderer={MediaListRenderer}
      postfix={MediaListRendererChildren}
    />
  ));

  return (
    <div
      className={classNames(styles.MediaUploader, className)}
      id={elementId}>
      {isMediaUploaderVisible && (
        <>
          <Button
            elementId={`${elementId}_button`}
            className={styles.button}
            isLoading={isLoading}
            type={ButtonType.SECONDARY}
            onClick={onClickUpload}>
            {buttonTitle}
          </Button>

          <div {...dropzone.getRootProps({ style: { display: 'none' } })}>
            <input
              data-testid={DATA_TEST_IDS.INPUT(elementId)}
              {...dropzone.getInputProps()}
            />
          </div>

          <span className={styles.desc}>{description}</span>
        </>
      )}

      {isSortableEnabled && onReorder ? (
        <ReactSortable<SortableMediaUploaderItem>
          tag="ul"
          id={DATA_TEST_IDS.LIST(elementId)}
          list={sortableItems}
          handle={`.${styles.dragHandle}`}
          forceFallback
          direction="vertical"
          disabled={!isDragHandleVisible}
          setList={onReorder}
          data-testid={DATA_TEST_IDS.LIST(elementId)}
          className={styles.list}>
          {mediaListItems}
        </ReactSortable>
      ) : (
        <ul
          id={DATA_TEST_IDS.LIST(elementId)}
          data-testid={DATA_TEST_IDS.LIST(elementId)}
          className={styles.list}>
          {mediaListItems}
        </ul>
      )}
    </div>
  );
};

export default VideoMediaUploader;
